import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Booking, Vehicle, BookingPayment, BookingAdjustment } from '@limo/database'
import {
  OPERATOR_DISCOUNT_SOURCE,
  allocateAmountByShare,
  computeDiscountAmount,
  discountLabel,
  isOperatorDiscountLine,
  type DiscountLineItem,
  type QuoteDiscount,
} from '@limo/shared'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  MoreHorizontal,
  ExternalLink,
  XCircle,
  Car,
  FileText,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Users,
  Plane,
  Loader2,
  CheckCircle,
  AlertCircle,
  Play,
  Wallet,
  ArrowLeftRight,
} from 'lucide-react'

// Import booking components
import {
  BookingNotesSection,
  BookingCostBreakdown,
  BookingPaymentsSection,
  AddAdjustmentDialog,
  ChargeCustomerDialog,
  RefundDialog,
  PricingAuditCard,
} from '@/components/bookings'
import { ApplyDiscountDialog } from '@/components/discounts'
import { DepositModeControl, GenerateBalanceLinkDialog } from '@/components/deposits'
import type { PaymentMode } from '@limo/shared/pricing'
import { GHLSyncLogsSection } from '@/components/integrations/GHLSyncLogsSection'
import { ZapierSyncLogsSection } from '@/components/integrations/ZapierSyncLogsSection'
import { GoogleAdsConversionSection } from '@/components/integrations/GoogleAdsConversionSection'
import { TrafficSourceCard } from '@/components/bookings/TrafficSourceCard'

// Extended booking type with vehicle relation
type BookingWithVehicle = Booking & {
  vehicles: Pick<Vehicle, 'id' | 'name'> | null
}

// Status badge variants
const bookingStatusStyles: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  pending: { variant: 'secondary', label: 'Pending' },
  confirmed: { variant: 'default', label: 'Confirmed' },
  in_progress: { variant: 'default', label: 'In Progress' },
  completed: { variant: 'outline', label: 'Completed' },
  cancelled: { variant: 'destructive', label: 'Cancelled' },
  no_show: { variant: 'destructive', label: 'No Show' },
}

const paymentStatusStyles: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  pending: { variant: 'secondary', label: 'Pending' },
  paid: { variant: 'default', label: 'Paid' },
  refunded: { variant: 'outline', label: 'Refunded' },
  failed: { variant: 'destructive', label: 'Failed' },
}

const roundCents = (value: number) => Math.round(value * 100) / 100

// Format helpers
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

const formatDateTime = (dateStr: string, timezone?: string | null) =>
  new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  })

const formatServiceType = (
  type: string,
  isAirportPickup?: boolean | null,
  isAirportDropoff?: boolean | null,
) => {
  if ((isAirportPickup || isAirportDropoff) && type === 'point_to_point') {
    return 'Airport Transportation'
  }
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [statusChangeTarget, setStatusChangeTarget] = useState<string | null>(null)
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false)
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false)
  const [balanceLinkDialogOpen, setBalanceLinkDialogOpen] = useState(false)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const [adjustmentToDelete, setAdjustmentToDelete] = useState<string | null>(null)
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false)

  // Fetch booking with vehicle relation
  const {
    data: booking,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['booking-detail', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('bookings')
        .select(
          `
          *,
          vehicles (id, name)
        `
        )
        .eq('id', id)
        // Soft-deleted bookings are hidden — a stale URL reads as not found
        .is('deleted_at', null)
        .single()
      if (error) throw error
      return data as BookingWithVehicle
    },
    enabled: !!id,
  })

  // Fetch adjustments for this booking
  const { data: adjustments } = useQuery({
    queryKey: ['booking-adjustments', id],
    queryFn: async () => {
      if (!id) return []
      const { data, error } = await supabase
        .from('booking_adjustments')
        .select('*')
        .eq('booking_id', id)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data as BookingAdjustment[]
    },
    enabled: !!id,
  })

  // Fetch payments for this booking
  const { data: payments } = useQuery({
    queryKey: ['booking-payments', id],
    queryFn: async () => {
      if (!id) return []
      const { data, error } = await supabase
        .from('booking_payments')
        .select('*')
        .eq('booking_id', id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as BookingPayment[]
    },
    enabled: !!id,
  })

  // The quote this booking came from. Only needed for its discount: when a
  // discount is baked into `total_amount`, the quote row is the only place its
  // percentage still exists (the breakdown keeps just the dollar line).
  const { data: originQuote } = useQuery({
    queryKey: ['booking-origin-quote', id],
    queryFn: async () => {
      if (!id) return null
      const { data } = await supabase
        .from('booking_quotes')
        .select('discount_type, discount_value')
        .eq('booking_id', id)
        .maybeSingle()
      return data
    },
    enabled: !!id,
  })

  const originQuoteDiscount: QuoteDiscount | null =
    (originQuote?.discount_type === 'percent' || originQuote?.discount_type === 'flat') &&
    Number(originQuote.discount_value) > 0
      ? { type: originQuote.discount_type, value: Number(originQuote.discount_value) }
      : null

  // For paired round trips we need the sibling leg's total + payments so
  // the Total / Paid / Balance figures reflect the combined round-trip
  // charge instead of just this single leg. Without this, legacy paired
  // rows that recorded the full combined payment on one of the two legs
  // render a phantom "Credit" when the per-leg total ($P or P-D) is
  // compared against the combined charge ($2P-D).
  const siblingId = booking?.paired_booking_id ?? null

  const { data: siblingBooking } = useQuery({
    queryKey: ['booking-sibling', siblingId],
    queryFn: async () => {
      if (!siblingId) return null
      const { data, error } = await supabase
        .from('bookings')
        // `pricing_breakdown` is needed because a discount on a round trip is
        // applied to the pair, not to one leg — see the discount block below.
        .select(
          'id, total_amount, amount_paid, payment_status, stripe_payment_intent_id, pricing_breakdown'
        )
        .eq('id', siblingId)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!siblingId,
  })

  const { data: siblingPayments } = useQuery({
    queryKey: ['booking-payments', siblingId],
    queryFn: async () => {
      if (!siblingId) return []
      const { data, error } = await supabase
        .from('booking_payments')
        .select('*')
        .eq('booking_id', siblingId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as BookingPayment[]
    },
    enabled: !!siblingId,
  })

  // Calculate totals
  const adjustmentsTotal =
    adjustments?.reduce((sum, adj) => {
      const amount = Number(adj.amount)
      return sum + (adj.adjustment_type === 'discount' || adj.adjustment_type === 'refund_credit' ? -amount : amount)
    }, 0) || 0

  const succeededPayments = payments?.filter((p) => p.status === 'succeeded') || []
  const refundedPayments = payments?.filter((p) => p.status === 'refunded') || []

  const paymentsTotal = succeededPayments.reduce(
    (sum, p) => sum + Number(p.amount),
    0
  )

  // Refunds are stored as negative amounts
  const totalRefunded = refundedPayments.reduce(
    (sum, p) => sum + Math.abs(Number(p.amount)),
    0
  )

  // Check if the original payment is already recorded in booking_payments
  const originalPaymentRecorded = payments?.some(
    (p) => p.stripe_payment_intent_id === booking?.stripe_payment_intent_id
  )

  // For backwards compatibility: include original payment if paid but not in booking_payments
  const hasUnrecordedOriginalPayment =
    booking?.payment_status === 'paid' &&
    booking?.stripe_payment_intent_id &&
    !originalPaymentRecorded

  const effectivePaymentsTotal = hasUnrecordedOriginalPayment
    ? paymentsTotal + Number(booking?.amount_paid || booking?.total_amount || 0)
    : paymentsTotal

  const adjustedTotal = (booking ? Number(booking.total_amount) : 0) + adjustmentsTotal
  const balanceDue = adjustedTotal - effectivePaymentsTotal

  // Any money at all having moved settles the payment option. Checked against
  // the ledger rather than `payment_status` so a partially-recorded payment
  // still locks it.
  const hasBeenPaid =
    effectivePaymentsTotal > 0 ||
    Number(booking?.amount_paid || 0) > 0 ||
    booking?.payment_status === 'paid'

  // The account's own mode, so "inherit" can say what it resolves to.
  const { data: accountPaymentSettings } = useQuery({
    queryKey: ['account-payment-settings', booking?.account_id],
    queryFn: async () => {
      if (!booking?.account_id) return {}
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', booking.account_id)
        .in('key', ['booking_payment_mode', 'booking_require_deposit'])
      if (error) throw error
      return {
        paymentMode: data?.find((s) => s.key === 'booking_payment_mode')?.value,
        requireDeposit: data?.find((s) => s.key === 'booking_require_deposit')?.value,
      }
    },
    enabled: !!booking?.account_id,
  })

  // Override the payment mode for this booking alone. Only meaningful while it
  // is unpaid — create-payment-intent and /api/booking/payment-mode both resolve
  // this column, so it decides what the customer is actually charged.
  const depositModeMutation = useMutation({
    mutationFn: async (mode: PaymentMode | null) => {
      if (!id) throw new Error('Booking ID is required')
      const { error } = await supabase
        .from('bookings')
        .update({ deposit_mode_override: mode } as never)
        .eq('id', id)
      if (error) throw error
      return mode
    },
    onSuccess: (mode) => {
      queryClient.invalidateQueries({ queryKey: ['booking-detail', id] })
      toast({
        title: mode ? 'Payment option updated' : 'Following the account default',
        description: mode
          ? 'This booking will use the option you picked.'
          : 'This booking now uses whatever the account is set to.',
      })
    },
    onError: (mutationError) => {
      toast({
        title: 'Error updating the payment option',
        description: mutationError.message,
        variant: 'destructive',
      })
    },
  })

  // ---------------------------------------------------------------------------
  // The booking's discount
  //
  // Held as a single `type: 'discount'` line inside `pricing_breakdown`, exactly
  // where checkout puts a quote discount. Editing rewrites that line and
  // `total_amount` so the breakdown always reads as one discount at its current
  // value — no correction rows to reconcile by eye.
  //
  // `isOperatorDiscountLine` is what keeps this safe: `type: 'discount'` alone
  // would also match `Return Trip Discount (15%)`, and a negative
  // `type: 'adjustment'` line ("Summer Discount (-20%)") is a pricing RULE that
  // stays inside the undiscounted total and is never touched.
  // ---------------------------------------------------------------------------
  const breakdownLines: DiscountLineItem[] = Array.isArray(booking?.pricing_breakdown)
    ? (booking.pricing_breakdown as DiscountLineItem[])
    : []

  const discountLine = breakdownLines.find(isOperatorDiscountLine)
  const lineDiscountAmount = roundCents(Math.abs(Number(discountLine?.amount) || 0))

  // This leg with no discount on it. Legacy rows sit outside `total_amount` and
  // are deleted by the next edit, so they don't belong in this base.
  const legPreDiscountTotal = roundCents(
    (booking ? Number(booking.total_amount) : 0) + lineDiscountAmount
  )

  // A paired round trip is one trip charged once across two rows, so its
  // discount belongs to the PAIR: the operator discounts "the trip", and the
  // amount is split across both legs (see `discountMutation`). Every figure the
  // dialog works from is therefore the combined one.
  const siblingBreakdownLines: DiscountLineItem[] = Array.isArray(
    siblingBooking?.pricing_breakdown
  )
    ? (siblingBooking.pricing_breakdown as DiscountLineItem[])
    : []
  const siblingDiscountAmount = roundCents(
    Math.abs(Number(siblingBreakdownLines.find(isOperatorDiscountLine)?.amount) || 0)
  )
  const siblingPreDiscountTotal = siblingBooking
    ? roundCents(Number(siblingBooking.total_amount) + siblingDiscountAmount)
    : 0

  // What the trip costs with no discount on it — the base every percentage is
  // measured against, so an edit re-bases instead of compounding.
  const preDiscountTotal = roundCents(legPreDiscountTotal + siblingPreDiscountTotal)

  // Legacy correction rows from the earlier adjustment-based model. Folded away
  // (deleted) on the next edit so a booking never carries both representations.
  const legacyDiscountRow = adjustments?.find(
    (adj) =>
      (adj as { discount_type?: string | null }).discount_type != null ||
      adj.adjustment_type === 'discount'
  )

  // Signed effect of a legacy row: a discount row subtracts, a clawback row adds.
  const legacyRowEffect = legacyDiscountRow
    ? legacyDiscountRow.adjustment_type === 'discount'
      ? -Number(legacyDiscountRow.amount)
      : Number(legacyDiscountRow.amount)
    : 0

  // What is discounted right now, net of any legacy correction — so a booking
  // mid-migration reports the discount the operator actually sees on the page.
  // On a paired round trip both legs carry their share, so both count.
  const currentDiscountAmount = roundCents(
    lineDiscountAmount + siblingDiscountAmount - legacyRowEffect
  )

  // The percent/flat to prefill. The line's own metadata is the truth once the
  // Builder has written it; before that, a legacy row or the originating quote is
  // the only place the percentage still exists (the line keeps just dollars).
  const lineMetadata = discountLine?.metadata as
    | { discountType?: unknown; discountValue?: unknown }
    | null
    | undefined
  const legacyRowType = (legacyDiscountRow as { discount_type?: string | null } | undefined)
    ?.discount_type
  const legacyRowValue = Number(
    (legacyDiscountRow as { discount_value?: number | null } | undefined)?.discount_value ??
      Number.NaN
  )

  const bookingDiscount: QuoteDiscount | null = (() => {
    if (currentDiscountAmount <= 0) return null
    if (lineMetadata?.discountType === 'percent' || lineMetadata?.discountType === 'flat') {
      const value = Number(lineMetadata.discountValue)
      if (value > 0) return { type: lineMetadata.discountType, value }
    }
    if ((legacyRowType === 'percent' || legacyRowType === 'flat') && legacyRowValue > 0) {
      return { type: legacyRowType, value: legacyRowValue }
    }
    if (originQuoteDiscount) return originQuoteDiscount
    return { type: 'flat', value: currentDiscountAmount }
  })()

  // Discounting is blocked once nothing is owed — reducing the total on a fully
  // paid booking would only create an overpayment. Refund instead.
  const discountBlockedReason =
    balanceDue <= 0 && currentDiscountAmount <= 0
      ? 'This booking is fully paid. Use Refund to return money to the customer.'
      : undefined

  // Cancel booking mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!id) return
      const { error } = await supabase
        .from('bookings')
        .update({
          booking_status: 'cancelled',
          cancelled_at: new Date().toISOString(),
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-detail', id] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      toast({
        title: 'Booking cancelled',
        description: 'The booking has been cancelled.',
      })
      setCancelDialogOpen(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Status change mutation
  const statusChangeMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      if (!id) return
      const { error } = await supabase
        .from('bookings')
        .update({
          booking_status: newStatus,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-detail', id] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      toast({
        title: 'Status updated',
        description: 'The booking status has been updated.',
      })
      setStatusChangeTarget(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
      setStatusChangeTarget(null)
    },
  })

  // Apply / edit / clear the booking's discount.
  //
  // Rewrites the discount line in `pricing_breakdown` and `total_amount` in place,
  // so 10% -> 2% leaves ONE line reading "Discount (2%)" and a total to match. The
  // percentage is measured against `preDiscountTotal`, never against the already
  // discounted figure.
  //
  // A paired round trip is discounted as ONE trip: the amount comes off the
  // combined total and is then split across the two legs pro-rata, so each row
  // reads honestly on its own and the pair still sums to what the operator
  // promised. Discounting a single leg would have quietly halved the discount
  // the customer was told about.
  //
  // What's preserved: `subtotal`, `gratuity_amount` and `taxes` are untouched — a
  // discount comes off the grand total, exactly as at checkout — and the payments
  // ledger (`amount_paid`, `booking_payments`) still records what was actually
  // charged, so a rewrite on a paid booking simply produces a chargeable balance
  // or a refundable credit. The originally quoted discount survives on the quote
  // row and in `pricing_audit`.
  const discountMutation = useMutation({
    mutationFn: async (discount: QuoteDiscount | null) => {
      if (!id) throw new Error('Booking ID is required')

      const target = discount ? computeDiscountAmount(preDiscountTotal, discount) : 0

      if (discount) {
        if (target <= 0) throw new Error('Enter a discount greater than zero')
        if (target >= preDiscountTotal) {
          throw new Error('The discount cannot be the same as or more than the total')
        }
      }

      // Drop any correction row from the earlier model so the two representations
      // can never both be live on one booking.
      if (legacyDiscountRow) {
        const { error: deleteError } = await supabase
          .from('booking_adjustments')
          .delete()
          .eq('id', legacyDiscountRow.id)
        if (deleteError) throw deleteError
      }

      // Split by each leg's undiscounted total. `allocateAmountByShare` puts any
      // leftover cent on the larger leg, so the shares add back up to `target`
      // exactly — the same helper checkout uses to split a quote discount.
      const [legShare, siblingShare] = siblingBooking
        ? allocateAmountByShare(target, [legPreDiscountTotal, siblingPreDiscountTotal])
        : [target, 0]

      const buildBreakdown = (lines: DiscountLineItem[], share: number) => {
        const withoutDiscount = lines.filter((line) => !isOperatorDiscountLine(line))
        if (!discount || share <= 0) return withoutDiscount
        return [
          ...withoutDiscount,
          {
            label: discountLabel(discount),
            amount: -share,
            type: 'discount',
            // Marks the line as ours and keeps the operator's percent, which a
            // dollar amount alone can't express.
            metadata: {
              source: OPERATOR_DISCOUNT_SOURCE,
              discountType: discount.type,
              discountValue: discount.value,
            },
          },
        ]
      }

      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          pricing_breakdown: buildBreakdown(breakdownLines, legShare) as never,
          total_amount: roundCents(legPreDiscountTotal - legShare),
        })
        .eq('id', id)

      if (updateError) throw updateError

      if (siblingBooking) {
        const { error: siblingError } = await supabase
          .from('bookings')
          .update({
            pricing_breakdown: buildBreakdown(siblingBreakdownLines, siblingShare) as never,
            total_amount: roundCents(siblingPreDiscountTotal - siblingShare),
          })
          .eq('id', siblingBooking.id)

        // The customer-facing total is the pair's, so a half-written discount is
        // worse than none: surface it instead of leaving the legs disagreeing.
        if (siblingError) {
          throw new Error(
            `The discount saved on this leg but not the other one (${siblingError.message}). Reapply it so both legs match.`
          )
        }
      }

      return discount
    },
    onSuccess: (discount) => {
      queryClient.invalidateQueries({ queryKey: ['booking-detail', id] })
      queryClient.invalidateQueries({ queryKey: ['booking-adjustments', id] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      // The other leg's total changed too, so its cached row is now stale.
      if (siblingId) {
        queryClient.invalidateQueries({ queryKey: ['booking-sibling', siblingId] })
      }
      setDiscountDialogOpen(false)
      toast({
        title: discount ? 'Discount updated' : 'Discount removed',
        description: discount
          ? siblingBooking
            ? 'The discount is split across both legs of the round trip, and the balance due reflects it.'
            : 'The cost breakdown, balance due and any charge you take reflect the new discount.'
          : siblingBooking
            ? 'Both legs of this round trip are back to full price.'
            : 'This booking is back to its full price.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error saving discount',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete adjustment mutation
  const deleteAdjustmentMutation = useMutation({
    mutationFn: async (adjustmentId: string) => {
      const { error } = await supabase
        .from('booking_adjustments')
        .delete()
        .eq('id', adjustmentId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-adjustments', id] })
      queryClient.invalidateQueries({ queryKey: ['booking-detail', id] })
      toast({
        title: 'Adjustment removed',
        description: 'The adjustment has been removed from this booking.',
      })
      setAdjustmentToDelete(null)
    },
    onError: (error) => {
      toast({
        title: 'Error removing adjustment',
        description: error.message,
        variant: 'destructive',
      })
      setAdjustmentToDelete(null)
    },
  })

  // Handler for remove adjustment button
  const handleRemoveAdjustment = (adjustmentId: string) => {
    setAdjustmentToDelete(adjustmentId)
  }

  // Get adjustment details for delete confirmation dialog
  const adjustmentToDeleteDetails = adjustments?.find(
    (adj) => adj.id === adjustmentToDelete
  )

  // Available status transitions based on current status
  const getAvailableStatusTransitions = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return [
          { status: 'confirmed', label: 'Confirm Booking', icon: CheckCircle },
        ]
      case 'confirmed':
        return [
          { status: 'in_progress', label: 'Start Trip', icon: Play },
        ]
      case 'in_progress':
        return [
          { status: 'completed', label: 'Complete Trip', icon: CheckCircle },
          { status: 'no_show', label: 'Mark No Show', icon: AlertCircle },
        ]
      default:
        return []
    }
  }

  if (!activeAccount || !id) {
    return null
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <Skeleton className="h-10 w-10" />
            <div>
              <Skeleton className="h-8 w-48" />
              <div className="mt-2 flex gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          </div>
          <Skeleton className="h-10 w-24" />
        </div>

        {/* Content skeleton */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error || !booking) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium mb-2">Booking not found</p>
        <p className="text-muted-foreground mb-4">
          The booking you're looking for doesn't exist or you don't have access to it.
        </p>
        <Button variant="outline" onClick={() => navigate(`${basePath}/bookings`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Bookings
        </Button>
      </div>
    )
  }

  const availableTransitions = getAvailableStatusTransitions(booking.booking_status)
  const canCancel =
    booking.booking_status !== 'cancelled' &&
    booking.booking_status !== 'completed' &&
    booking.booking_status !== 'no_show'

  // Parse pricing breakdown from JSONB
  const pricingBreakdown = Array.isArray(booking.pricing_breakdown)
    ? booking.pricing_breakdown
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`${basePath}/bookings`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">
                Booking #{booking.confirmation_code}
              </h1>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  bookingStatusStyles[booking.booking_status]?.variant || 'secondary'
                }
              >
                {bookingStatusStyles[booking.booking_status]?.label ||
                  booking.booking_status}
              </Badge>
              <Badge
                variant={
                  paymentStatusStyles[booking.payment_status]?.variant || 'secondary'
                }
              >
                {paymentStatusStyles[booking.payment_status]?.label ||
                  booking.payment_status}
              </Badge>
              {booking.deposit_required && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <Wallet className="h-3 w-3 mr-1" />
                  Deposit ({booking.deposit_percentage}%)
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                Created {formatDateTime(booking.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {/* Status change buttons */}
          {availableTransitions.map((transition) => (
            <Button
              key={transition.status}
              variant="outline"
              onClick={() => setStatusChangeTarget(transition.status)}
              disabled={statusChangeMutation.isPending}
            >
              <transition.icon className="mr-2 h-4 w-4" />
              {transition.label}
            </Button>
          ))}

          {/* Actions dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  const viewerDomain =
                    import.meta.env.VITE_VIEWER_DOMAIN || 'localhost:3001'
                  const protocol = viewerDomain.includes('localhost') ? 'http' : 'https'
                  window.open(
                    `${protocol}://${activeAccount.slug}.${viewerDomain}`,
                    '_blank'
                  )
                }}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                View Website
              </DropdownMenuItem>
              {canCancel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Cancel Booking
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Paired round-trip banner with a link to the sibling leg's detail
          page. This is the new model: each leg is its own `bookings` row with
          its own confirmation code, pricing, and integration sync record. */}
      {booking.paired_booking_id && (
        <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
          <ArrowLeftRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <p className="text-sm font-medium text-blue-800 flex-1">
            {booking.trip_leg === 'outbound'
              ? 'This is the OUTBOUND leg of a round trip.'
              : booking.trip_leg === 'return'
                ? 'This is the RETURN leg of a round trip.'
                : 'This is one leg of a round trip.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(`${basePath}/bookings/${booking.paired_booking_id}`)}
            className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline"
          >
            View {booking.trip_leg === 'outbound' ? 'return' : 'outbound'} leg →
          </button>
        </div>
      )}

      {/* Legacy round-trip banner — only shown for old single-row round trips
          that pre-date the paired-booking model (return_trip=true AND
          paired_booking_id IS NULL). New round trips render the banner above. */}
      {booking.return_trip && !booking.paired_booking_id && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
          <ArrowLeftRight className="h-4 w-4 text-green-600 flex-shrink-0" />
          <p className="text-sm font-medium text-green-700">
            Round Trip — Price includes both outbound and return legs.
          </p>
        </div>
      )}

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Trip Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Trip Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3">
                <Car className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Vehicle</p>
                  <p className="font-medium">{booking.vehicles?.name || 'Unknown'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Service Type</p>
                  <p className="font-medium">{formatServiceType(booking.service_type, booking.is_airport_pickup, booking.is_airport_dropoff)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date & Time</p>
                  <p className="font-medium">{formatDateTime(booking.pickup_datetime, activeAccount?.timezone)}</p>
                </div>
              </div>
              {booking.return_trip && booking.return_datetime && (
                <div className="flex items-start gap-3">
                  <ArrowLeftRight className="h-4 w-4 mt-1 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Return Date & Time</p>
                    <p className="font-medium">{formatDateTime(booking.return_datetime, activeAccount?.timezone)}</p>
                  </div>
                </div>
              )}
              {booking.duration_hours && (
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-medium">{booking.duration_hours} hours</p>
                  </div>
                </div>
              )}
              {booking.distance_miles && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Distance</p>
                    <p className="font-medium">{booking.distance_miles} miles</p>
                  </div>
                </div>
              )}
            </div>

            {/* Locations */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-green-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Pickup</p>
                  <p className="font-medium">{booking.pickup_address}</p>
                </div>
              </div>
              {booking.dropoff_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-1 text-red-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Dropoff</p>
                    <p className="font-medium">{booking.dropoff_address}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{booking.customer_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <a
                    href={`mailto:${booking.customer_email}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {booking.customer_email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <a
                    href={`tel:${booking.customer_phone}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {booking.customer_phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="h-4 w-4 mt-1 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Passengers</p>
                  <p className="font-medium">{booking.passenger_count}</p>
                </div>
              </div>
            </div>

            {/* Flight info (for airport trips) */}
            {(booking.is_airport_pickup || booking.is_airport_dropoff) &&
              (booking.airline || booking.flight_number) && (
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3">
                    <Plane className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Flight Information</p>
                      <p className="font-medium">
                        {[booking.airline, booking.flight_number]
                          .filter(Boolean)
                          .join(' - ')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* Special requests */}
            {booking.special_requests && (
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Special Requests</p>
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm whitespace-pre-wrap">{booking.special_requests}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* GoHighLevel Sync History */}
      <GHLSyncLogsSection bookingId={booking.id} />

      {/* Zapier Sync History */}
      <ZapierSyncLogsSection bookingId={booking.id} />

      {/* Where this booking came from, then whether we told Google about it —
          the two belong together. */}
      <TrafficSourceCard booking={booking} />

      {/* Google Ads conversion upload */}
      <GoogleAdsConversionSection bookingId={booking.id} />

      {/* Internal Notes */}
      <BookingNotesSection
        bookingId={booking.id}
        initialNotes={booking.internal_notes}
      />

      {/* Financial Section - Cost Breakdown and Payments side by side on large screens */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Cost Breakdown — for paired round-trip legs we hide the payment
            footer so the per-leg total isn't compared against the combined
            charge (which would show a phantom credit). The Payments card on
            the right becomes the single source of truth for paid/balance. */}
        <BookingCostBreakdown
          isRoundTrip={booking.return_trip === true}
          isPairedBooking={!!booking.paired_booking_id}
          pricingBreakdown={pricingBreakdown}
          subtotal={Number(booking.subtotal)}
          gratuityAmount={Number(booking.gratuity_amount)}
          gratuityPercent={Number(booking.gratuity_percent)}
          taxes={Number(booking.taxes)}
          totalAmount={Number(booking.total_amount)}
          adjustmentsTotal={adjustmentsTotal}
          paymentsTotal={effectivePaymentsTotal}
          onAddAdjustment={canCancel ? () => setAdjustmentDialogOpen(true) : undefined}
          onApplyDiscount={
            canCancel && !discountBlockedReason
              ? () => setDiscountDialogOpen(true)
              : undefined
          }
          discountBlockedReason={canCancel ? discountBlockedReason : undefined}
          hasDiscount={currentDiscountAmount > 0}
          adjustments={adjustments || []}
          onRemoveAdjustment={canCancel ? handleRemoveAdjustment : undefined}
        />

        {/* Deposit override for this booking alone.
            Locked once anything has been paid: the charge already happened, so
            restating what "should" have been collected would only misreport it.
            Still rendered read-only so the operator can see what was applied. */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Payment Option
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DepositModeControl
              value={(booking as { deposit_mode_override?: PaymentMode | null }).deposit_mode_override ?? null}
              onChange={(mode) => depositModeMutation.mutate(mode)}
              accountSettings={accountPaymentSettings ?? {}}
              disabled={hasBeenPaid}
              disabledReason={
                'This booking has been paid, so the payment option is fixed. ' +
                'To change what is owed, apply a discount or take the balance.'
              }
              isSaving={depositModeMutation.isPending}
            />
          </CardContent>
        </Card>

        {/* Payments Section. For paired round trips we display combined
            Total / Paid / Balance across both legs so the figures match
            Stripe's actual charge, no matter how booking_payments was
            recorded (legacy single combined entry OR new per-leg split). */}
        <BookingPaymentsSection
          bookingId={booking.id}
          totalAmount={
            siblingBooking
              ? Number(booking.total_amount) + Number(siblingBooking.total_amount)
              : Number(booking.total_amount)
          }
          adjustmentsTotal={adjustmentsTotal}
          additionalPayments={siblingPayments || []}
          onChargeClick={
            booking.stripe_payment_intent_id && balanceDue > 0
              ? () => setChargeDialogOpen(true)
              : undefined
          }
          // No saved-card requirement: the whole point of a link is that the
          // customer supplies a card, so it works where Charge Customer can't.
          onPaymentLinkClick={
            balanceDue > 0 ? () => setBalanceLinkDialogOpen(true) : undefined
          }
          onRefundClick={
            booking.stripe_payment_intent_id
              ? () => setRefundDialogOpen(true)
              : undefined
          }
          originalPaymentAmount={Number(booking.amount_paid || booking.total_amount)}
          originalPaymentStatus={booking.payment_status}
          originalPaymentIntentId={booking.stripe_payment_intent_id || undefined}
        />
      </div>

      {/* Pricing Audit Trail */}
      <PricingAuditCard pricingAudit={booking.pricing_audit as never} />

      {/* Dialogs */}
      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the booking as cancelled. If payment has been processed, you
              may need to issue a refund through your payment processor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Booking'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Confirmation Dialog */}
      <AlertDialog
        open={!!statusChangeTarget}
        onOpenChange={() => setStatusChangeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change booking status to{' '}
              {statusChangeTarget &&
                bookingStatusStyles[statusChangeTarget]?.label.toLowerCase()}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will update the booking status. You can change it again later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                statusChangeTarget && statusChangeMutation.mutate(statusChangeTarget)
              }
              disabled={statusChangeMutation.isPending}
            >
              {statusChangeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Adjustment Confirmation Dialog */}
      <AlertDialog
        open={!!adjustmentToDelete}
        onOpenChange={(open) => !open && setAdjustmentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this adjustment?</AlertDialogTitle>
            <AlertDialogDescription>
              {adjustmentToDeleteDetails ? (
                <>
                  This will remove &quot;{adjustmentToDeleteDetails.label}&quot; (
                  {formatCurrency(Number(adjustmentToDeleteDetails.amount))}) from the
                  booking. The balance due will be recalculated.
                </>
              ) : (
                'This will remove the adjustment from the booking.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                adjustmentToDelete &&
                deleteAdjustmentMutation.mutate(adjustmentToDelete)
              }
              disabled={deleteAdjustmentMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAdjustmentMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Adjustment'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Adjustment Dialog */}
      <AddAdjustmentDialog
        bookingId={booking.id}
        currentTotal={Number(booking.total_amount) + adjustmentsTotal}
        open={adjustmentDialogOpen}
        onOpenChange={setAdjustmentDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['booking-adjustments', id] })
        }}
      />

      {/* Apply Discount. Same UI as the quote page; written into
          `pricing_breakdown` + `total_amount` so every total already accounts
          for it. On a paired round trip the figures here are the pair's, and
          the saved discount is split across both legs. */}
      <ApplyDiscountDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        preDiscountTotal={preDiscountTotal}
        currentDiscount={bookingDiscount}
        currentDiscountAmount={currentDiscountAmount}
        entityLabel={siblingBooking ? 'round trip (both legs)' : 'booking'}
        isPending={discountMutation.isPending}
        onApply={(discount) => discountMutation.mutate(discount)}
        onClear={() => discountMutation.mutate(null)}
      />

      {/* Pay-Balance Link. Keyed to the OUTBOUND leg of a round trip so one
          link settles the pair — the viewer resolves the combined balance from
          whichever row it is given, but generating from both legs would create
          two payable links for one trip. */}
      <GenerateBalanceLinkDialog
        open={balanceLinkDialogOpen}
        onOpenChange={setBalanceLinkDialogOpen}
        bookingId={
          booking.trip_leg === 'return' && booking.paired_booking_id
            ? booking.paired_booking_id
            : booking.id
        }
        accountId={booking.account_id}
        accountSlug={activeAccount.slug}
        accountCustomDomain={activeAccount.custom_domain}
        balanceDue={balanceDue}
        pickupDatetime={booking.pickup_datetime}
        onGenerated={() => {
          queryClient.invalidateQueries({ queryKey: ['booking-detail', id] })
        }}
      />

      {/* Charge Customer Dialog */}
      <ChargeCustomerDialog
        bookingId={booking.id}
        balanceDue={balanceDue}
        open={chargeDialogOpen}
        onOpenChange={setChargeDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['booking-payments', id] })
        }}
      />

      {/* Refund Dialog */}
      <RefundDialog
        bookingId={booking.id}
        totalPaid={effectivePaymentsTotal}
        totalRefunded={totalRefunded}
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['booking-payments', id] })
        }}
      />
    </div>
  )
}
